/**
 * DPR-aware canvas setup + RAF loop helpers.
 *
 * Spec requirements covered here:
 *   1. Retina sharpness — canvas.width/height are scaled by devicePixelRatio
 *      and ctx.scale(dpr, dpr) is applied. Re-runs on every resize via
 *      ResizeObserver, so rotating the phone or moving between displays keeps
 *      the rendering crisp.
 *   2. Frame-rate independence — RafLoop clamps dt to 33 ms (≈30 fps floor)
 *      and exposes `dt` to the callback. Every position/velocity update done
 *      inside the callback multiplies by dt.
 */

export interface CanvasView {
  ctx: CanvasRenderingContext2D;
  /** Logical CSS pixel size — what your draw code should think in. */
  cssWidth: number;
  cssHeight: number;
  dpr: number;
  dispose: () => void;
}

/**
 * Configure a canvas for retina rendering. Returns a view that re-syncs
 * automatically on resize. Call view.dispose() when the component unmounts.
 */
export function attachCanvas(canvas: HTMLCanvasElement): CanvasView {
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D context unavailable");

  const view: CanvasView = {
    ctx,
    cssWidth: 0,
    cssHeight: 0,
    dpr: 1,
    dispose: () => {
      /* set below */
    },
  };

  const sync = (): void => {
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    const cssW = Math.max(1, Math.round(rect.width));
    const cssH = Math.max(1, Math.round(rect.height));
    canvas.width = cssW * dpr;
    canvas.height = cssH * dpr;
    // Reset transform before scaling — ctx.scale is multiplicative.
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);
    view.cssWidth = cssW;
    view.cssHeight = cssH;
    view.dpr = dpr;
  };

  sync();

  const ro = new ResizeObserver(sync);
  ro.observe(canvas);
  window.addEventListener("orientationchange", sync);

  view.dispose = (): void => {
    ro.disconnect();
    window.removeEventListener("orientationchange", sync);
  };
  return view;
}

/**
 * Frame-rate independent requestAnimationFrame loop.
 *
 * Per spec: const dt = Math.min((timestamp - last) / 1000, 0.033).
 * The 33 ms ceiling absorbs tab-throttling pauses without producing a
 * giant single-frame jump.
 */
export class RafLoop {
  private rafId = 0;
  private last = 0;
  private running = false;

  constructor(private readonly onFrame: (dt: number, timestamp: number) => void) {}

  start(): void {
    if (this.running) return;
    this.running = true;
    this.last = performance.now();
    const tick = (timestamp: DOMHighResTimeStamp): void => {
      if (!this.running) return;
      const dt = Math.min((timestamp - this.last) / 1000, 0.033);
      this.last = timestamp;
      this.onFrame(dt, timestamp);
      this.rafId = requestAnimationFrame(tick);
    };
    this.rafId = requestAnimationFrame(tick);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.rafId);
  }
}
